import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../primitives/Icon';

export interface NavItem {
  label: string;
  icon: string;
  to: string;
  badge?: { text: string; tone?: 'default' | 'accent' | 'danger' } | null;
  dot?: boolean;
  exact?: boolean;
  /** Optional child entries — turns this item into a collapsible group.
   *  The parent's `to` remains the primary destination; the dropdown
   *  surfaces sub-pages that share its section in the app. */
  children?: NavItem[];
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

export const DEFAULT_NAV: NavGroup[] = [
  {
    title: 'ראשי',
    items: [
      { label: 'דשבורד',    icon: 'dashboard',   to: '/',             exact: true },
      { label: 'לקוחות',     icon: 'groups',      to: '/customers' },
      { label: 'עסקאות',     icon: 'work',        to: '/deals' },
      { label: 'הצעות מחיר', icon: 'description', to: '/quotes' },
      { label: 'חברות',      icon: 'apartment',   to: '/companies' },
    ],
  },
];

interface SidebarProps {
  nav?: NavGroup[];
  companyName?: string;
  /** Controlled open state for mobile drawer. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  nav = DEFAULT_NAV,
  companyName = 'מטת טכנולוגיות',
  mobileOpen = false,
  onMobileClose,
}) => {
  const location = useLocation();

  // Auto-close on route change (mobile)
  useEffect(() => {
    if (mobileOpen && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Body scroll lock while open on mobile
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileOpen]);

  const isActive = (to: string, exact?: boolean) => {
    if (exact) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={onMobileClose}
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'w-64 bg-white dark:bg-slate-950 border-e border-slate-200 dark:border-slate-800',
          'flex flex-col fixed start-0 top-0 bottom-0 z-50',
          'transition-transform duration-200 ease-out',
          // Always visible on desktop. On mobile: hidden by default (translated out to the start side).
          // In RTL start = right, so we translate positive X to push off-screen right.
          'lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        {/* Logo */}
        <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
          <Link to="/" className="flex items-center justify-center flex-1 min-w-0">
            <img src="/matat-logo.svg" alt="Matat" className="h-10 w-auto" />
          </Link>
          {/* Close button on mobile */}
          <button
            onClick={onMobileClose}
            className="lg:hidden size-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-500"
            aria-label="סגור תפריט"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-5">
          {nav.map((group) => (
            <div key={group.title}>
              <div className="px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
                {group.title}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavEntry key={item.to} item={item} isActive={isActive} />
                ))}
              </div>
            </div>
          ))}
        </nav>

      </aside>
    </>
  );
};

// A single nav row. When the item has `children`, it renders a
// collapsible group: clicking the chevron toggles the submenu, while
// clicking the row itself still navigates to the parent route.
// Auto-expands when the parent route OR any child route is active.
const NavEntry: React.FC<{
  item: NavItem;
  isActive: (to: string, exact?: boolean) => boolean;
}> = ({ item, isActive }) => {
  const active = isActive(item.to, item.exact);
  const hasChildren = !!(item.children && item.children.length > 0);
  const childActive = hasChildren && item.children!.some(c => isActive(c.to, c.exact));
  const [expanded, setExpanded] = useState(active || childActive);

  // Re-expand when navigation lands inside the section from outside the
  // sidebar (e.g. deep link), without collapsing a user's manual choice.
  useEffect(() => {
    if (childActive) setExpanded(true);
  }, [childActive]);

  return (
    <div>
      <div className="relative flex items-stretch">
        <Link
          to={item.to}
          className={[
            'flex-1 flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors',
            active
              ? 'text-primary bg-primary/10'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-white',
          ].join(' ')}
        >
          {active && <span className="absolute end-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-s-full" />}
          <Icon name={item.icon} size={20} />
          <span className="flex-1">{item.label}</span>
          {item.badge && (
            <span
              className={[
                'text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                item.badge.tone === 'accent'
                  ? 'bg-accent/10 text-accent-dark'
                  : item.badge.tone === 'danger'
                    ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
              ].join(' ')}
            >
              {item.badge.text}
            </span>
          )}
          {item.dot && !item.badge && <span className="size-2 rounded-full bg-accent" />}
        </Link>
        {hasChildren && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setExpanded(v => !v); }}
            className="ms-1 size-9 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 flex items-center justify-center transition-colors"
            aria-label={expanded ? 'סגור תת-תפריט' : 'פתח תת-תפריט'}
            aria-expanded={expanded}
          >
            <Icon name={expanded ? 'expand_less' : 'expand_more'} size={18} />
          </button>
        )}
      </div>

      {hasChildren && expanded && (
        <div className="mt-0.5 ms-7 ps-2 border-s border-slate-100 dark:border-slate-800 space-y-0.5">
          {item.children!.map(child => {
            const childIsActive = isActive(child.to, child.exact);
            return (
              <Link
                key={child.to}
                to={child.to}
                className={[
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors',
                  childIsActive
                    ? 'text-primary bg-primary/5'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-white',
                ].join(' ')}
              >
                <Icon name={child.icon} size={15} />
                <span className="flex-1 truncate">{child.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
